/**
 * @file VariantTable.tsx
 *
 * The per-variant editor for a variable product, mirroring WSB's VariantsTable: a
 * collapsible "Variants" header captioned with the generated-combination count, the bulk
 * column-fill ("Set all variants → Apply to all") directly beneath it, and one row per
 * generated combination — image chooser leftmost, combination label, bare-numeric price
 * and sales-price fields (currency lives in the column HEADERS), a minus/value/plus stock
 * stepper with the unlimited (∞) toggle demoted below it, SKU, and a trailing delete.
 * The fixed-layout table always fits its card: no horizontal scroll, no weight column.
 *
 * Controlled: the row set is owned by the form and reported via `onChange`; the write
 * path diffs the rows against their baseline and batches every dirty one into a single
 * call at save time (nothing debounces). A combination the merchant has defined but not
 * yet saved carries a synthetic id and an "Unsaved" tag; it cannot be deleted (it is
 * defined by the options until saved). Deleting a persisted row is a MARK, not a write:
 * the row dims in place with an Undo and becomes permanent only when the form saves — and
 * the last sellable row cannot be marked (the engine refuses a product with no enabled
 * variant). Duplicate SKUs are flagged live, folded the way the engine folds them. The
 * image cell picks from the product's own gallery images.
 */

import { useState } from 'react';
import { Icon, StandardDialog, Toggle } from '@group.one/react-gravity';
import { FormattedMessage, useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import type { ShopProductVariant } from '../../../api/shop-types';
import { duplicateSkuVariantIds, type VariantRowErrors } from '../product-form-utils';
import { isNewVariantRow } from '../variant-matrix';
import { ImageOrDefault } from '../../../components/ImageOrDefault';
import { ConfirmModal } from '../../../components/ConfirmModal';
import { VariantBulkFill } from './VariantBulkFill';
import styles from './VariantTable.module.css';

/** Maps a per-field variant error reason to its message descriptor. */
const PRICE_ERROR_MESSAGE = {
  required: adminMessages.formVariantRowPriceRequired,
  invalid: adminMessages.formVariantRowPriceInvalid,
} as const;
const SALE_ERROR_MESSAGE = {
  invalid: adminMessages.formVariantRowSaleInvalid,
  tooHigh: adminMessages.formVariantRowSaleTooHigh,
} as const;
const STOCK_ERROR_MESSAGE = {
  invalid: adminMessages.formVariantRowStockInvalid,
} as const;

interface VariantTableProps {
  variants: ShopProductVariant[];
  onChange: (next: ShopProductVariant[]) => void;
  currencySymbol: string;
  /** The product's own gallery images, the only images a variant may be assigned. */
  galleryImages: { id: string; src: string }[];
  /** The live cartesian count of the current axes (for the over-cap backstop). */
  combinationCount: number;
  /** The hard ceiling on generated variants (100). */
  maxCombinations: number;
  /**
   * Per-row validation errors keyed by variant id, surfaced inline under the offending
   * cell on a blocked save. Empty (the default) means no row has an error.
   */
  errors?: Record<string, VariantRowErrors>;
}

/**
 * Renders the per-variant table for a variable product.
 *
 * @param props - The variant rows, change handler, currency, gallery, and count limits.
 *
 * @returns The variant table (or an empty-state hint when no variants exist yet).
 */
export function VariantTable({
  variants,
  onChange,
  currencySymbol,
  galleryImages,
  combinationCount,
  maxCombinations,
  errors = {},
}: VariantTableProps) {
  const intl = useIntl();
  const [retiring, setRetiring] = useState<ShopProductVariant | null>(null);
  const [pickingImageFor, setPickingImageFor] = useState<ShopProductVariant | null>(null);
  // Collapsible so a product with a long matrix does not push everything below it off
  // the screen; opens expanded.
  const [collapsed, setCollapsed] = useState(false);

  const patchRow = (id: string, patch: Partial<ShopProductVariant>): void => {
    onChange(variants.map((v) => (v.id === id ? { ...v, ...patch } : v)));
  };

  // The bulk column-fill is an ordinary buffered edit applied to every sellable row at
  // once; a row already marked for deletion is left alone.
  const bulkFill = (patch: Partial<ShopProductVariant>): void => {
    onChange(variants.map((v) => (v.enabled ? { ...v, ...patch } : v)));
  };

  const setUnlimited = (id: string, unlimited: boolean): void => {
    patchRow(
      id,
      unlimited
        ? { manage_stock: false, stock_quantity: null }
        : { manage_stock: true, stock_quantity: 0 },
    );
  };

  const confirmRetire = (): void => {
    if (retiring) {
      patchRow(retiring.id, { enabled: false });
    }
    setRetiring(null);
  };

  const chooseImage = (image: { id: string; src: string } | null): void => {
    if (pickingImageFor) {
      patchRow(pickingImageFor.id, {
        image: image ? { id: image.id, src: image.src, alt: pickingImageFor.label } : null,
      });
    }
    setPickingImageFor(null);
  };

  const sellableCount = variants.filter((v) => v.enabled).length;
  const overLimit = combinationCount > maxCombinations;
  // A product must keep at least one sellable variant, so the last unmarked row's delete
  // is held back here rather than left to fail the save (the engine's floor).
  const canMarkMore = sellableCount > 1;
  // Duplicate SKUs are flagged as the merchant types, judged across sellable rows only —
  // catching them here matters because the engine rejects the whole batch over one.
  const duplicateSkus = duplicateSkuVariantIds(variants.filter((v) => v.enabled));

  // The currency lives in the price column headers rather than inside every input, so
  // the fields stay bare numeric with a "00.00" ghost (the WSB arrangement).
  const withCurrency = (label: string): string =>
    currencySymbol ? `${label} (${currencySymbol})` : label;

  return (
    <div className="gv-flex gv-flex-col gv-gap-md gv-w-full" data-testid="variant-table">
      <button
        type="button"
        className={styles.collapseHeader}
        onClick={() => setCollapsed((value) => !value)}
        aria-expanded={!collapsed}
        data-testid="variant-table-collapse"
      >
        <span className="gv-flex gv-flex-col gv-items-start gv-gap-xs">
          <span className="gv-text-bold">
            <FormattedMessage {...adminMessages.formVariantTableTitle} />
          </span>
          <span
            className="gv-text-sm gv-text-muted"
            data-testid="variant-generated-count"
          >
            <FormattedMessage
              {...adminMessages.formVariantsGeneratedCount}
              values={{ count: variants.length }}
            />
          </span>
        </span>
        <Icon name={collapsed ? 'expand_more' : 'expand_less'} size="small" aria-hidden />
      </button>

      {overLimit && (
        <p className="gv-text-sm gv-state-critical" role="alert" data-testid="variant-too-many">
          <FormattedMessage
            {...adminMessages.formVariantTooMany}
            values={{ count: combinationCount, max: maxCombinations }}
          />
        </p>
      )}

      {!collapsed && variants.length === 0 && (
        <p className="gv-text-sm gv-text-muted" data-testid="variant-empty">
          <FormattedMessage {...adminMessages.formVariantEmpty} />
        </p>
      )}

      {!collapsed && variants.length > 0 && (
        <>
          {/* The bulk column-fill sits between the header and the grid, so a merchant
              sets a whole column before scanning the rows (mirrors WSB's placement). */}
          <VariantBulkFill onApply={bulkFill} currencySymbol={currencySymbol} />
          <div className={styles.scroll}>
            <table className={styles.table} data-testid="variant-rows">
              <thead>
                <tr>
                  <th>
                    <span className="gv-sr-only">
                      <FormattedMessage {...adminMessages.formVariantColImage} />
                    </span>
                  </th>
                  <th className="gv-text-sm">
                    <FormattedMessage {...adminMessages.formVariantColVariant} />
                  </th>
                  <th className="gv-text-sm">
                    {withCurrency(intl.formatMessage(adminMessages.formVariantColPrice))}
                  </th>
                  <th className="gv-text-sm">
                    {withCurrency(
                      intl.formatMessage(adminMessages.formVariantColSalePrice),
                    )}
                  </th>
                  <th className="gv-text-sm">
                    <FormattedMessage {...adminMessages.formVariantColStock} />
                  </th>
                  <th className="gv-text-sm">
                    <FormattedMessage {...adminMessages.formVariantColSku} />
                  </th>
                  <th aria-label={intl.formatMessage(adminMessages.formVariantColActions)} />
                </tr>
              </thead>
              <tbody>
                {variants.map((variant) => {
                  const rowError = errors[variant.id];
                  const isNew = isNewVariantRow(variant);
                  const locked = !variant.enabled;
                  const stockQuantity = variant.stock_quantity ?? 0;
                  const skuDuplicate =
                    rowError?.sku === 'duplicate' || duplicateSkus.has(variant.id);
                  return (
                    <tr
                      key={variant.id}
                      className={variant.enabled ? undefined : styles.retired}
                      data-testid="variant-row"
                      data-id={variant.id}
                    >
                      <td>
                        <button
                          type="button"
                          className={styles.imageButton}
                          data-testid="variant-image"
                          disabled={locked || galleryImages.length === 0}
                          title={
                            galleryImages.length === 0
                              ? intl.formatMessage(
                                  adminMessages.formVariantImageNeedsGallery,
                                )
                              : undefined
                          }
                          aria-label={intl.formatMessage(adminMessages.formVariantSetImage)}
                          onClick={() => setPickingImageFor(variant)}
                        >
                          {variant.image ? (
                            <img
                              src={variant.image.src}
                              alt=""
                              className={styles.imageThumb}
                            />
                          ) : (
                            <Icon name="add_photo_alternate" size="small" aria-hidden />
                          )}
                        </button>
                      </td>

                      <td className={styles.nameCell} title={variant.label}>
                        <span className={styles.nameCellLayout}>
                          <span className={`gv-text-sm ${styles.nameLabel}`}>
                            {variant.label}
                          </span>
                          {isNew && (
                            <span
                              className={styles.unsavedTag}
                              data-testid="variant-unsaved-tag"
                            >
                              <FormattedMessage {...adminMessages.formVariantUnsavedTag} />
                            </span>
                          )}
                        </span>
                      </td>

                      <td>
                        <input
                          className={`gv-input gv-text-sm ${styles.cellInput}`}
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="00.00"
                          aria-label={intl.formatMessage(adminMessages.formVariantColPrice)}
                          data-testid="variant-price"
                          value={variant.regular_price}
                          disabled={locked}
                          onChange={(e) =>
                            patchRow(variant.id, { regular_price: e.target.value })
                          }
                        />
                        {rowError?.price && (
                          <p
                            className={`gv-text-sm gv-state-critical ${styles.cellError}`}
                            role="alert"
                            data-testid="variant-price-error"
                          >
                            <FormattedMessage {...PRICE_ERROR_MESSAGE[rowError.price]} />
                          </p>
                        )}
                      </td>

                      <td>
                        <input
                          className={`gv-input gv-text-sm ${styles.cellInput}`}
                          inputMode="decimal"
                          autoComplete="off"
                          placeholder="00.00"
                          aria-label={intl.formatMessage(
                            adminMessages.formVariantColSalePrice,
                          )}
                          data-testid="variant-sale-price"
                          value={variant.sale_price}
                          disabled={locked}
                          onChange={(e) =>
                            patchRow(variant.id, { sale_price: e.target.value })
                          }
                        />
                        {rowError?.salePrice && (
                          <p
                            className={`gv-text-sm gv-state-critical ${styles.cellError}`}
                            role="alert"
                            data-testid="variant-sale-price-error"
                          >
                            <FormattedMessage {...SALE_ERROR_MESSAGE[rowError.salePrice]} />
                          </p>
                        )}
                      </td>

                      <td>
                        {/* Stepper on top, the unlimited switch stacked beneath it — a
                            minus/value/plus control that reads as the stock field, with
                            the ∞ toggle demoted below rather than beside it. */}
                        <div className="gv-flex gv-flex-col gv-gap-xs gv-items-start">
                          <div
                            className={`gv-input gv-input-number ${styles.stepperBox} ${
                              variant.manage_stock ? '' : 'gv-disabled'
                            }`}
                          >
                            <button
                              type="button"
                              className={`gv-button gv-button-bare ${styles.stepperButton}`}
                              disabled={locked || !variant.manage_stock || stockQuantity <= 0}
                              data-testid="variant-stock-decrease"
                              aria-label={intl.formatMessage(
                                adminMessages.formVariantStockDecrease,
                              )}
                              onClick={() =>
                                patchRow(variant.id, {
                                  stock_quantity: Math.max(0, stockQuantity - 1),
                                })
                              }
                            >
                              <Icon name="remove" size="small" aria-hidden />
                            </button>
                            {variant.manage_stock ? (
                              <input
                                type="number"
                                min={0}
                                className={`gv-text-sm ${styles.stockInput}`}
                                aria-label={intl.formatMessage(
                                  adminMessages.formVariantColStock,
                                )}
                                data-testid="variant-stock"
                                value={stockQuantity}
                                disabled={locked}
                                onChange={(e) =>
                                  patchRow(variant.id, {
                                    stock_quantity: Math.max(
                                      0,
                                      Number.parseInt(e.target.value, 10) || 0,
                                    ),
                                  })
                                }
                              />
                            ) : (
                              <span
                                className={`gv-text-sm ${styles.stockInfinity}`}
                                aria-hidden="true"
                              >
                                ∞
                              </span>
                            )}
                            <button
                              type="button"
                              className={`gv-button gv-button-bare ${styles.stepperButton}`}
                              disabled={locked || !variant.manage_stock}
                              data-testid="variant-stock-increase"
                              aria-label={intl.formatMessage(
                                adminMessages.formVariantStockIncrease,
                              )}
                              onClick={() =>
                                patchRow(variant.id, { stock_quantity: stockQuantity + 1 })
                              }
                            >
                              <Icon name="add" size="small" aria-hidden />
                            </button>
                          </div>
                          <span
                            data-testid="variant-unlimited"
                            title={intl.formatMessage(
                              adminMessages.formVariantUnlimitedToggle,
                            )}
                          >
                            <Toggle
                              condensed
                              checked={!variant.manage_stock}
                              onChange={(checked) => setUnlimited(variant.id, checked)}
                            />
                          </span>
                        </div>
                        {rowError?.stock && (
                          <p
                            className={`gv-text-sm gv-state-critical ${styles.cellError}`}
                            role="alert"
                            data-testid="variant-stock-error"
                          >
                            <FormattedMessage {...STOCK_ERROR_MESSAGE[rowError.stock]} />
                          </p>
                        )}
                      </td>

                      <td>
                        <input
                          className={`gv-input gv-text-sm ${styles.cellInput}`}
                          autoComplete="off"
                          spellCheck={false}
                          placeholder="SKU-12345"
                          aria-label={intl.formatMessage(adminMessages.formVariantColSku)}
                          data-testid="variant-sku"
                          value={variant.sku}
                          disabled={locked}
                          onChange={(e) => patchRow(variant.id, { sku: e.target.value })}
                        />
                        {variant.enabled && skuDuplicate && (
                          <p
                            className={`gv-text-sm gv-state-critical ${styles.cellError}`}
                            role="alert"
                            data-testid="variant-sku-error"
                          >
                            <FormattedMessage {...adminMessages.formVariantSkuDuplicate} />
                          </p>
                        )}
                      </td>

                      <td>
                        {variant.enabled ? (
                          <button
                            type="button"
                            className={`gv-button gv-button-bare ${styles.rowAction}`}
                            data-testid="variant-retire"
                            aria-label={intl.formatMessage(adminMessages.formVariantRetire)}
                            disabled={isNew || !canMarkMore}
                            title={
                              isNew
                                ? intl.formatMessage(adminMessages.formVariantDeleteUnsaved)
                                : canMarkMore
                                  ? undefined
                                  : intl.formatMessage(
                                      adminMessages.formVariantRetireLastVariant,
                                    )
                            }
                            onClick={() => setRetiring(variant)}
                          >
                            <Icon name="delete" size="small" />
                          </button>
                        ) : (
                          // A retired row is an unsaved MARK until the form saves (the
                          // loaded rows are always sellable — the read path filters
                          // retired ones), so it can always be taken back.
                          <button
                            type="button"
                            className={`gv-button gv-button-tertiary gv-text-sm ${styles.rowAction}`}
                            data-testid="variant-undo-retire"
                            onClick={() => patchRow(variant.id, { enabled: true })}
                          >
                            <FormattedMessage {...adminMessages.formVariantUndo} />
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {retiring && (
        <ConfirmModal
          title={intl.formatMessage(adminMessages.formVariantRetireTitle, {
            name: retiring.label,
          })}
          message={intl.formatMessage(adminMessages.formVariantRetireMessage)}
          confirmLabel={intl.formatMessage(adminMessages.formVariantRetireConfirm)}
          cancelLabel={intl.formatMessage(adminMessages.formCancel)}
          loadingLabel={intl.formatMessage(adminMessages.formVariantRetireConfirm)}
          confirmVariant="destructive"
          onPrimaryClick={confirmRetire}
          onSecondaryClick={() => setRetiring(null)}
          onClose={() => setRetiring(null)}
        />
      )}

      {pickingImageFor && (
        <StandardDialog
          isOpen
          title={intl.formatMessage(adminMessages.formVariantChangeImage)}
          onClose={() => setPickingImageFor(null)}
          onClickAway={() => setPickingImageFor(null)}
          onEscape={() => setPickingImageFor(null)}
          footer={
            <div className="gv-button-group">
              <button
                type="button"
                className="gv-button gv-button-cancel"
                data-testid="variant-image-clear"
                onClick={() => chooseImage(null)}
              >
                <Icon name="delete" size="small" />
              </button>
            </div>
          }
        >
          <div className={styles.pickerGrid} data-testid="variant-image-picker">
            {galleryImages.map((image) => (
              <button
                key={image.id}
                type="button"
                className={`${styles.pickerTile} ${
                  pickingImageFor.image?.id === image.id ? styles.pickerTileSelected : ''
                }`}
                data-testid="variant-image-option"
                onClick={() => chooseImage(image)}
              >
                <ImageOrDefault src={image.src} alt="" className={styles.pickerImage} />
              </button>
            ))}
          </div>
        </StandardDialog>
      )}
    </div>
  );
}
